import { useMemo, useState } from 'react';

import { api } from '../services/api';
import {
  isCustomSpecKind,
  type ValidationResult,
} from '../services/formatSpecValidation';
import type {
  FormatSpecCandidate,
  HitlActionRequest,
  HitlActionType,
  HitlBatchConfirmRequest,
  HitlItem,
  HitSeverity,
} from '../services/types';
import { useLocalStorageBoolean } from '../services/useLocalStorageBoolean';
import { whyAsking } from '../services/whyAsking';

import { Button } from './Button';
import { CustomSpecInput } from './CustomSpecInput';
import {
  HitlBulkAckSweeper,
  MIN_RECOMMENDED_FOR_SWEEPER,
} from './HitlBulkAckSweeper';
import {
  MixedTypeSelector,
  type MixedTypeSelection,
} from './MixedTypeSelector';
import { JsonFlattenCard } from './JsonFlattenCard';
import { SeriesCadenceCard } from './SeriesCadenceCard';
import { AddressFormatCard } from './AddressFormatCard';
import { HeaderPiiConfirmationCard } from './HeaderPiiConfirmationCard';
import { MultiDateSplitCard } from './MultiDateSplitCard';
import { PdfTableSelectionCard } from './PdfTableSelectionCard';
import { QualityGateCard } from './QualityGateCard';
import { RuleCard } from './RuleCard';
import { UnitConversionCard } from './UnitConversionCard';

export interface HitlQueueProps {
  hits: HitlItem[];
  /**
   * Called when the reviewer dispatches an action. Implementers should
   * resolve the returned promise only once the backend accepts (so the row
   * can display a resolving state).
   */
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  /**
   * Called when the reviewer clicks "Confirm all" in the bulk-ack sweeper.
   * Implementers MUST refresh the hits cache after the POST resolves —
   * unlike single-action ``onAction`` where ``onHitAction`` in
   * ``RunDetailPage`` already chains ``fetchHits``, the bulk path
   * historically called ``api.postBatchConfirm`` directly from this
   * component and left the cache stale (v0.6 Stage F.4 bug: sweeper
   * stayed visible after dispatch because ``grouped.recommended.length``
   * never dropped). When the prop is omitted (legacy unit-test
   * mounts), we fall back to the direct API call so existing render-only
   * tests keep working — real call sites in the app MUST supply this.
   */
  onBatchConfirm?: (
    request: HitlBatchConfirmRequest & { run_id: string },
  ) => Promise<void>;
  /** Reviewer identifier — attached to every outgoing action. */
  reviewerId?: string;
  /** Optional filter for hit severity. */
  severityFilter?: HitSeverity | 'all';
  /** Optional filter for hit tier (v0.3 Stage F). */
  tierFilter?: 'recommended' | 'unclear' | 'all';
}

const severityStyles: Record<HitSeverity, string> = {
  low: 'bg-brand/15 text-brand-dark',
  medium: 'bg-warn/15 text-warn',
  high: 'bg-danger/15 text-danger',
};

// v0.3 Stage F — two-tier layout. ``recommended`` cards pre-fill the
// detected type/spec next to a one-click confirm. ``unclear`` cards
// surface the full option list + sample rows for reviewer judgement.
// A legacy ``null`` tier (pre-v0.3 rows + non-tiered items like the
// run-scoped date_output_format HITL, or PII items) falls through to
// the "unclear" layout without the warn tint — same mechanics,
// neutral styling. v0.5 Stage G.2 retired the last advisory emitter
// (PII uncovered-discovery), so HitlClass="advisory" no longer
// appears on freshly-emitted hits — the type literal stays for audit
// fidelity on historical rows but the queue treats it identically to
// blocking.
type RenderTier = 'recommended' | 'unclear' | 'legacy';

function renderTierOf(hit: HitlItem): RenderTier {
  if (hit.hitl_tier === 'recommended') return 'recommended';
  if (hit.hitl_tier === 'unclear') return 'unclear';
  return 'legacy';
}

const tierStyles: Record<RenderTier, { row: string; label: string | null }> = {
  recommended: {
    row: 'border-l-4 border-l-brand border-border',
    label: 'recommended',
  },
  unclear: {
    row: 'border-l-4 border-l-warn border-border',
    label: 'unclear',
  },
  legacy: {
    row: 'border-border',
    label: null,
  },
};

// Sentinel we inject into the radiogroup so reviewers can select a free-
// type spec alongside the detector-provided candidates.
const CUSTOM_SENTINEL = '__custom__';

// v0.5 Stage C.2 — when RAS surfaces ``mixed_types`` in the HITL
// options list, picking the radio reveals the MixedTypeSelector
// (checkbox group of the other detector candidates + a primary-type
// pick). The radio value is the literal token RAS emitted; we use a
// constant to avoid magic-string drift between the radio render and
// the dispatch builder.
const MIXED_TYPES_OPTION = 'mixed_types';

// v0.5 Stage C.2 — these tokens never make it into the
// MixedTypeSelector's checkbox group: ``mixed_types`` itself is the
// trigger; ``no_format_required`` is the reviewer's escape hatch and
// has its own button alongside Confirm; ``string`` is allowed in the
// selector because it acts as the catch-all in the picked union.
const MIXED_TYPE_SELECTOR_EXCLUDED = new Set<string>([
  MIXED_TYPES_OPTION,
  'no_format_required',
]);

// v0.6 Stage B1 — reviewer-facing labels for PII strategy tokens. The
// backend keeps the canonical short tokens (mask / clean / drop /
// pass_through / ignore) on the wire; this map exists purely so the
// HITL card reads like prose to the reviewer. Tokens absent from the
// map render verbatim (used by non-PII column_datatype options like
// "integer" / "date" etc.).
export const PII_OPTION_LABELS: Record<string, string> = {
  mask: 'Redact data from output',
  clean: 'Strip PII substrings',
  drop: 'Drop column',
  pass_through: 'Pass through unchanged',
  ignore: 'Ignore (no treatment)',
  // v0.6 Stage B17 — PII clean sub-mode tokens. The discover phase
  // emits these on type-specific options (e.g. clean:postcode_area_only
  // for postcode columns); each maps to a reviewer-facing label so the
  // radio reads like prose instead of the wire token.
  'clean:year_only': 'Clean: year only',
  'clean:country_code_only': 'Clean: country code only',
  'clean:domain_only': 'Clean: email domain only',
  'clean:last_4_only': 'Clean: last 4 digits only',
  'clean:first_initial_only': 'Clean: first initial only',
  'clean:postcode_area_only': 'Clean: postcode area only',
  'clean:postcode_full': 'Clean: standardise full postcode',
};

/**
 * Listing of pending HITL items for a run.
 *
 * v0.3 Stage F — every card carries a "No format required" escape
 * alongside the tier-specific confirm path.
 *
 * v0.5 Stage D — tier-grouped layout:
 *   1. Bulk-ack sweeper (when ≥2 recommended-tier cards open)
 *   2. Recommended-tier section (collapsed-by-default cards)
 *   3. Unclear-tier section (expanded cards)
 *   4. Cross-field rule section (existing v0.4 rendering)
 */
export function HitlQueue({
  hits,
  onAction,
  onBatchConfirm,
  reviewerId = 'anon',
  severityFilter = 'all',
  tierFilter = 'all',
}: HitlQueueProps) {
  // v0.5 Stage D — sweeper dismissal is session-scoped state. When
  // the reviewer clicks "Review individually", the sweeper hides for
  // this render but reappears on a fresh queue load (e.g. tab switch
  // back).
  const [sweeperDismissed, setSweeperDismissed] = useState(false);
  const [sweeperRunId, setSweeperRunId] = useState<string | null>(null);

  const filtered = hits.filter((h) => {
    if (severityFilter !== 'all' && h.severity !== severityFilter) return false;
    if (tierFilter !== 'all' && h.hitl_tier !== tierFilter) return false;
    return true;
  });

  // v0.5 Stage D — split into tier sections. Cross-field rule cards
  // get their own family because RuleCard is a separate component;
  // legacy / other-resolution-type cards fall through to
  // unclear-style rendering for now.
  const grouped = useMemo(() => {
    const recommended: HitlItem[] = [];
    const unclear: HitlItem[] = [];
    const crossField: HitlItem[] = [];
    const unitConversion: HitlItem[] = [];
    const jsonFlatten: HitlItem[] = [];
    const seriesCadence: HitlItem[] = [];
    const addressFormat: HitlItem[] = [];
    const multiDateSplit: HitlItem[] = [];
    const pdfTableSelection: HitlItem[] = [];
    const headerPiiConfirmation: HitlItem[] = [];
    const qualityGate: HitlItem[] = [];
    const other: HitlItem[] = [];
    for (const hit of filtered) {
      if (hit.resolution_type === 'cross_field_rule') {
        crossField.push(hit);
      } else if (hit.resolution_type === 'unit_conversion') {
        unitConversion.push(hit);
      } else if (hit.resolution_type === 'json_flatten') {
        jsonFlatten.push(hit);
      } else if (hit.resolution_type === 'series_cadence') {
        seriesCadence.push(hit);
      } else if (hit.resolution_type === 'address_format') {
        addressFormat.push(hit);
      } else if (hit.resolution_type === 'multi_date_split') {
        multiDateSplit.push(hit);
      } else if (hit.resolution_type === 'pdf_table_selection') {
        pdfTableSelection.push(hit);
      } else if (hit.resolution_type === 'header_pii_confirmation') {
        headerPiiConfirmation.push(hit);
      } else if (hit.resolution_type === 'cleanser_quality_missing') {
        // v0.6 Stage B12 — missing_handler quality-gate HITL with the
        // interactive switch_to_mode / switch_to_constant sub-panels.
        qualityGate.push(hit);
      } else if (hit.hitl_tier === 'recommended') {
        recommended.push(hit);
      } else if (hit.hitl_tier === 'unclear') {
        unclear.push(hit);
      } else {
        other.push(hit);
      }
    }
    return {
      recommended,
      unclear,
      crossField,
      unitConversion,
      jsonFlatten,
      seriesCadence,
      addressFormat,
      multiDateSplit,
      pdfTableSelection,
      headerPiiConfirmation,
      qualityGate,
      other,
    };
  }, [filtered]);

  // v0.5 Stage D — bulk-ack covers recommended-tier RAS / FI cards;
  // cross-field rule cards have their own action set in RuleCard so
  // they're excluded from the sweeper. ``run_id`` is the same across
  // the queue (the page hydrates per-run); we read it from the first
  // hit and keep it cached so the sweeper's POST has a stable target.
  //
  // Derived BEFORE the early-return below (v0.6 Stage F.1) so the
  // collapse-state hook can key its localStorage entry off the real
  // run_id rather than a generic key. The hook itself can't live
  // inside the conditional (rules of hooks); putting the runId
  // derivation here keeps the hook call order stable.
  const runId =
    grouped.recommended[0]?.run_id ?? grouped.unclear[0]?.run_id ?? null;

  // v0.6 Stage F.1 — recommended-tier section default-collapses to a
  // single-row summary; click expands. Persisted in localStorage per
  // run_id so the reviewer's expand state survives reload + tab
  // switching. Fallback key on null runId (filtered.length === 0) is
  // never read in practice because the early-return below skips the
  // section entirely; the hook still has to be invoked unconditionally.
  // v0.6 Stage B8 (I10) — recommended-tier section defaults to
  // *expanded* so pending actions (e.g. measurement unit confirmation)
  // are immediately visible. Without this, manual testers reported the
  // pipeline "looking stalled" — the card was sitting collapsed with
  // no clear indication that something needed their input. The first
  // collapse / expand still persists per-run in localStorage so the
  // reviewer's explicit choice survives reload.
  const [recommendedCollapsed, toggleRecommendedCollapsed] =
    useLocalStorageBoolean(
      `raydn:hitl:recommended-collapsed:${runId ?? '__none__'}`,
      false,
    );

  if (filtered.length === 0) {
    return (
      <section aria-labelledby="hitl-title" className="space-y-3">
        <h2 id="hitl-title">HITL queue</h2>
        <p className="text-sm text-muted">
          No open items. The pipeline is clear — or hasn't flagged anything yet.
        </p>
      </section>
    );
  }

  if (runId !== null && runId !== sweeperRunId) {
    setSweeperRunId(runId);
    setSweeperDismissed(false);
  }
  const showSweeper =
    !sweeperDismissed &&
    runId !== null &&
    grouped.recommended.length >= MIN_RECOMMENDED_FOR_SWEEPER;

  const handleConfirmAll = async () => {
    if (runId === null) return;
    const hitIds = grouped.recommended.map((h) => h.hit_id);
    // v0.6 Stage F.4 — prefer the parent-supplied bulk handler so the
    // hits cache refreshes after the POST resolves. Without that, the
    // sweeper's headline (``recommendedCount``) stays stale: the backend
    // flips the rows to ``status="resolved"`` but the frontend never
    // re-fetches, so the sweeper visibly hangs on the old count until
    // a tab nav or page reload forces ``useRun``'s status-change
    // refetch to run. The fallback to a direct API call preserves the
    // v0.5 single-action behaviour for legacy unit-test mounts that
    // pass no ``onBatchConfirm`` — those tests don't click "Confirm
    // all" so the stale-cache bug never manifests there.
    if (onBatchConfirm) {
      await onBatchConfirm({
        run_id: runId,
        hit_ids: hitIds,
        reviewer_id: reviewerId,
      });
      return;
    }
    await api.postBatchConfirm(runId, {
      hit_ids: hitIds,
      reviewer_id: reviewerId,
    });
  };

  return (
    <section aria-labelledby="hitl-title" className="space-y-4">
      <h2 id="hitl-title">HITL queue</h2>

      {showSweeper && (
        <HitlBulkAckSweeper
          recommendedCount={grouped.recommended.length}
          onConfirmAll={handleConfirmAll}
          onDismiss={() => setSweeperDismissed(true)}
        />
      )}

      {grouped.recommended.length > 0 && (
        <div data-testid="hitl-section-recommended" className="space-y-3">
          {/*
            * v0.6 Stage F.1 — clickable header toggles a localStorage-
            * persisted collapse state, keyed per run_id. Default is
            * collapsed: the reviewer lands on a one-line summary
            * ("N recommended decisions — review or bulk-confirm") and
            * expands when ready. ``aria-expanded`` + ``aria-controls``
            * give screen readers a real disclosure pattern; the chevron
            * glyph is decorative (aria-hidden) since the section name
            * + state are conveyed through the button's text.
            */}
          <button
            type="button"
            onClick={toggleRecommendedCollapsed}
            aria-expanded={!recommendedCollapsed}
            aria-controls="hitl-section-recommended-list"
            data-testid="hitl-section-recommended-toggle"
            className="flex w-full items-baseline gap-2 text-left text-xs font-medium uppercase tracking-wide text-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded-sm"
          >
            <span aria-hidden="true" className="text-ink/60">
              {recommendedCollapsed ? '▶' : '▼'}
            </span>
            <span>Recommended ({grouped.recommended.length})</span>
            {recommendedCollapsed && (
              <span
                data-testid="hitl-section-recommended-summary"
                className="normal-case font-normal text-ink/80"
              >
                — {grouped.recommended.length}{' '}
                {grouped.recommended.length === 1 ? 'decision' : 'decisions'}{' '}
                ready: review or bulk-confirm
              </span>
            )}
          </button>
          {!recommendedCollapsed && (
            <ul
              id="hitl-section-recommended-list"
              className="space-y-3"
            >
              {grouped.recommended.map((h) => (
                <HitRow
                  key={h.hit_id}
                  hit={h}
                  onAction={onAction}
                  reviewerId={reviewerId}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      {grouped.unclear.length > 0 && (
        <div data-testid="hitl-section-unclear" className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            Needs your input ({grouped.unclear.length})
          </h3>
          <ul className="space-y-3">
            {grouped.unclear.map((h) => (
              <HitRow
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.other.length > 0 && (
        <div data-testid="hitl-section-other" className="space-y-3">
          <ul className="space-y-3">
            {grouped.other.map((h) => (
              <HitRow
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.crossField.length > 0 && (
        <div data-testid="hitl-section-cross-field" className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            Cross-field rules ({grouped.crossField.length})
          </h3>
          <ul className="space-y-3">
            {grouped.crossField.map((h) => (
              <RuleCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.unitConversion.length > 0 && (
        <div
          data-testid="hitl-section-unit-conversion"
          className="space-y-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            Unit conversion ({grouped.unitConversion.length})
          </h3>
          <ul className="space-y-3">
            {grouped.unitConversion.map((h) => (
              <UnitConversionCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.jsonFlatten.length > 0 && (
        <div
          data-testid="hitl-section-json-flatten"
          className="space-y-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            JSON flatten ({grouped.jsonFlatten.length})
          </h3>
          <ul className="space-y-3">
            {grouped.jsonFlatten.map((h) => (
              <JsonFlattenCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.seriesCadence.length > 0 && (
        <div
          data-testid="hitl-section-series-cadence"
          className="space-y-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            Series cadence ({grouped.seriesCadence.length})
          </h3>
          <ul className="space-y-3">
            {grouped.seriesCadence.map((h) => (
              <SeriesCadenceCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.addressFormat.length > 0 && (
        <div
          data-testid="hitl-section-address-format"
          className="space-y-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            Address format ({grouped.addressFormat.length})
          </h3>
          <ul className="space-y-3">
            {grouped.addressFormat.map((h) => (
              <AddressFormatCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.multiDateSplit.length > 0 && (
        <div
          data-testid="hitl-section-multi-date-split"
          className="space-y-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            Multi-date split ({grouped.multiDateSplit.length})
          </h3>
          <ul className="space-y-3">
            {grouped.multiDateSplit.map((h) => (
              <MultiDateSplitCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.pdfTableSelection.length > 0 && (
        <div
          data-testid="hitl-section-pdf-table-selection"
          className="space-y-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            PDF table selection ({grouped.pdfTableSelection.length})
          </h3>
          <ul className="space-y-3">
            {grouped.pdfTableSelection.map((h) => (
              <PdfTableSelectionCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.headerPiiConfirmation.length > 0 && (
        <div
          data-testid="hitl-section-header-pii-confirmation"
          className="space-y-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            Sensitive column review ({grouped.headerPiiConfirmation.length})
          </h3>
          <ul className="space-y-3">
            {grouped.headerPiiConfirmation.map((h) => (
              <HeaderPiiConfirmationCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}

      {grouped.qualityGate.length > 0 && (
        <div
          data-testid="hitl-section-quality-gate"
          className="space-y-3"
        >
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
            Missing-value review ({grouped.qualityGate.length})
          </h3>
          <ul className="space-y-3">
            {grouped.qualityGate.map((h) => (
              <QualityGateCard
                key={h.hit_id}
                hit={h}
                onAction={onAction}
                reviewerId={reviewerId}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// v0.6 Stage F.3 — focus the next/previous HITL card in the queue.
// Queries every ``data-hitl-card="true"`` element in the document
// (which is fine: there's only ever one HITL queue mounted at a time
// per route) and shifts focus by ``delta``. Boundary behaviour:
// no-wraparound — j on the last card or k on the first is a no-op.
// Wraparound would make navigation cyclic but invisibly so for
// reviewers who can't see the queue end position; staying put at the
// boundary is the friendlier default.
function focusCardSibling(current: HTMLElement, delta: number) {
  const cards = Array.from(
    document.querySelectorAll<HTMLElement>('[data-hitl-card="true"]'),
  );
  const idx = cards.indexOf(current);
  if (idx === -1) return;
  const nextIdx = idx + delta;
  if (nextIdx < 0 || nextIdx >= cards.length) return;
  cards[nextIdx].focus();
}

function HitRow({
  hit,
  onAction,
  reviewerId,
}: {
  hit: HitlItem;
  onAction: HitlQueueProps['onAction'];
  reviewerId: string;
}) {
  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  // v0.6 Stage F.3 — per-card collapse state, persisted in localStorage
  // keyed on hit_id. Default-expanded: when the reviewer opens the
  // recommended-tier section (F.1) or scrolls into the unclear tier,
  // they expect content visible. Collapsing is an explicit "I've
  // reviewed this, park it" gesture; persistence means a parked card
  // stays parked across reloads + tab switches.
  const [cardCollapsed, toggleCardCollapsed] = useLocalStorageBoolean(
    `raydn:hitl:card-collapsed:${hit.hit_id}`,
    false,
  );
  // Radio-group selection. On recommended tier it's only consulted once
  // the reviewer clicks "Not correct — specify" to reveal the group;
  // on unclear/legacy tiers the group is always visible.
  const [selected, setSelected] = useState<string | null>(null);
  // v0.5 Stage A: Custom-spec input is detector-aware — the
  // CustomSpecInput component holds the per-kind draft state internally
  // and notifies us via onChange with a validated `{ok, spec}` result.
  // We hold the latest result here so the Confirm button + dispatch
  // path can read it. ``null`` means the reviewer hasn't focused the
  // Custom radio yet OR the current draft is invalid.
  const [customSpecResult, setCustomSpecResult] =
    useState<ValidationResult | null>(null);
  // Recommended tier only — toggled by the "Not correct — specify"
  // button to reveal the full options / candidates radio group.
  const [specifyOpen, setSpecifyOpen] = useState(false);
  // v0.5 Stage C.2 — when the reviewer selects ``mixed_types`` from
  // the radio group, the MixedTypeSelector below the radio reports
  // the picked union + primary type via this state. Confirm reads
  // it to build the ``choose_mixed_types`` dispatch payload.
  const [mixedTypeSelection, setMixedTypeSelection] =
    useState<MixedTypeSelection | null>(null);

  // v0.6 Stage 1.x.2 — when a cleanser_quality HITL surfaces because
  // the reviewer's regex_mask spec rejected too many values, the
  // "adjust_spec" radio reveals an inline pattern editor pre-filled
  // with the original spec (so reviewers can iterate without retyping).
  // Holds the in-progress new pattern; null when adjust_spec hasn't
  // been touched yet.
  const [adjustSpecDraft, setAdjustSpecDraft] = useState<string | null>(null);

  // v0.4 Stage E.5: HitlItem.candidates is a union of FormatSpecCandidate
  // and RuleCandidate (the latter for cross-field rule HITLs). HitRow
  // only ever sees the FormatSpecCandidate flavour because the parent
  // routes rule HITLs to RuleCard before reaching here. Narrow once at
  // the top so the rest of the function works against the original
  // FormatSpecCandidate shape without per-line casts.
  const candidates = hit.candidates as FormatSpecCandidate[] | null | undefined;

  const hasOptions = Array.isArray(hit.options) && hit.options.length > 0;
  const hasCandidates =
    Array.isArray(candidates) && candidates.length > 0;
  // v0.6 Stage 1.x.2 — cleanser_quality HITLs (from cleaners that emit
  // via ``make_cleanser_quality_item``) carry a metadata-only candidate
  // with kind="cleanser_quality" and a real ``options`` list. Pre-1.x.2
  // they fell through to the format-spec radio renderer which printed
  // the candidate as ``"undefined · cleanser_quality"`` (no spec field).
  // Now we route them to the options-radio path and surface the
  // ``adjust_spec`` editor when the original spec is available.
  const isCleanserQuality =
    hasCandidates && (candidates![0] as { kind?: string }).kind === 'cleanser_quality';
  const cleanserOriginalSpec = isCleanserQuality
    ? ((candidates![0] as { original_spec?: { kind: string; spec: { pattern?: string } } })
        .original_spec ?? null)
    : null;
  const cleanserOriginalPattern = cleanserOriginalSpec?.spec?.pattern ?? '';
  const cleanserAdjustSpecText = adjustSpecDraft ?? cleanserOriginalPattern;
  // v0.6 Stage B9 (I9) — per-option descriptive suffixes from the
  // cleanser_quality candidate. The backend stashes the actual
  // replacement values (mode_value, constant_value) under
  // ``candidates[0].option_descriptions[option_key]`` so reviewers can
  // tell switch_to_mode and switch_to_constant apart. Empty map for
  // HITLs that don't carry per-option descriptions (the common case).
  const cleanserOptionDescriptions: Record<string, string> = isCleanserQuality
    ? (
        (candidates![0] as { option_descriptions?: Record<string, string> })
          .option_descriptions ?? {}
      )
    : {};
  const tier = renderTierOf(hit);
  const tierStyle = tierStyles[tier];
  const recommendedLabel = recommendedHeaderLabel(hit);

  // v0.5 Stage A: when the reviewer picks the Custom radio, Confirm
  // gates on a *validated* per-kind input — not just non-empty text.
  // A boolean column with True="Y" but no False label is "filled" but
  // not valid; the Confirm button must stay disabled.
  const customSpecValid = customSpecResult !== null && customSpecResult.ok;
  const pickerReady =
    hasCandidates &&
    selected !== null &&
    (selected !== CUSTOM_SENTINEL || customSpecValid);
  // v0.5 Stage C.2 — the mixed-types radio is only confirm-ready when
  // the reviewer has at least one type ticked in the selector.
  const mixedTypesValid =
    mixedTypeSelection !== null && mixedTypeSelection.types.length > 0;

  // The candidates' kind drives which Custom-input UI renders. The
  // Custom radio itself is hidden when the kind is one we don't yet
  // have a Custom UI for (notably ``pattern_none`` / ``none`` —
  // those reach the reviewer via the "No format required" button,
  // not the radio group). FormatInferrer's orchestrator dispatches
  // one detector per column so all candidates for a single hit share
  // the same kind; we read the first.
  const customSpecKind = candidates?.[0]?.kind;
  const customSpecAvailable =
    Boolean(hit.allow_custom) &&
    typeof customSpecKind === 'string' &&
    isCustomSpecKind(customSpecKind);

  // Confirm enablement:
  // * Recommended + !specifyOpen → enabled (pre-selected default).
  // * Recommended + specifyOpen → requires a pick (same rules as unclear).
  // * Unclear / legacy → requires a pick when options/candidates exist.
  // v0.6 Stage 1.x.2 — cleanser_quality renders options (not candidates)
  // even when ``hasCandidates`` is true, so the gate uses the
  // options-path conditions and additionally requires a non-empty
  // pattern when adjust_spec is selected.
  const cleanserAdjustSpecMissing =
    isCleanserQuality &&
    selected === 'adjust_spec' &&
    cleanserAdjustSpecText.trim().length === 0;
  const confirmDisabled =
    pending !== null ||
    (!isCleanserQuality && tier === 'recommended' && specifyOpen && hasCandidates && !pickerReady) ||
    (!isCleanserQuality && tier === 'recommended' && specifyOpen && !hasCandidates && hasOptions && selected === null) ||
    (!isCleanserQuality && tier !== 'recommended' && hasCandidates && !pickerReady) ||
    (!isCleanserQuality && tier !== 'recommended' && !hasCandidates && hasOptions && selected === null) ||
    (isCleanserQuality && selected === null) ||
    cleanserAdjustSpecMissing ||
    // v0.5 Stage C.2 — picking the mixed_types radio gates Confirm
    // on a non-empty selection from the MixedTypeSelector below.
    (selected === MIXED_TYPES_OPTION && !mixedTypesValid);

  const dispatch = async (action: HitlActionRequest) => {
    setPending(action.type);
    setError(null);
    try {
      await onAction(hit.hit_id, action);
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  const chooseFromSelection = (choice: string): HitlActionRequest => {
    // v0.6 Stage 1.x.2 — cleanser_quality option mappings. The HITL
    // exposes three reviewer-facing options; each maps to a different
    // backend action so the carry-forward path picks up the right
    // intent on the next pipeline pass:
    //   * adjust_spec     → choose_format_spec with the (edited)
    //                       original spec; the widened
    //                       ``last_format_spec_decision_for`` query
    //                       picks this up and re-enforces with the
    //                       new pattern on the next run.
    //   * no_format_required → no_format_required action; carry-forward
    //                          synthesises kind="none" so the cleanser
    //                          falls through to passthrough.
    //   * accept_nulls    → plain accept; the existing cleanser-quality
    //                       acknowledgement path suppresses re-emission
    //                       on subsequent uploads of the same bytes.
    if (isCleanserQuality) {
      if (choice === 'adjust_spec') {
        const kind = cleanserOriginalSpec?.kind ?? 'regex_mask';
        return {
          type: 'choose_format_spec',
          payload: {
            kind,
            spec: { pattern: cleanserAdjustSpecText },
          },
          reviewer_id: reviewerId,
        };
      }
      if (choice === 'no_format_required') {
        return { type: 'no_format_required', reviewer_id: reviewerId };
      }
      // accept_nulls (and any other future option) → plain accept.
      return { type: 'accept', reviewer_id: reviewerId };
    }
    // v0.5 Stage C.2 — reviewer picked ``mixed_types`` from the radio
    // group → dispatch ``choose_mixed_types`` with the picked union +
    // primary type from the MixedTypeSelector. Confirm enablement
    // (mixedTypesValid in confirmDisabled below) gates this path so
    // the selection is guaranteed non-empty when we reach here.
    if (choice === MIXED_TYPES_OPTION) {
      return {
        type: 'choose_mixed_types',
        payload: {
          types: mixedTypeSelection?.types ?? [],
          primary: mixedTypeSelection?.primary ?? null,
        },
        reviewer_id: reviewerId,
      };
    }
    // Reviewer picked from candidates → choose_format_spec with the
    // candidate's kind + spec (or custom pattern when the sentinel was
    // selected).
    if (hasCandidates) {
      let payload: { kind: string; spec: Record<string, unknown> };
      if (choice === CUSTOM_SENTINEL) {
        // v0.5 Stage A: payload comes from the validated per-kind
        // input held in `customSpecResult`. Confirm is gated on
        // `customSpecValid` upstream, so this branch is only reached
        // when the result is { ok: true, spec }.
        const kind = candidates?.[0]?.kind || 'regex_mask';
        const spec =
          customSpecResult && customSpecResult.ok
            ? customSpecResult.spec
            : {};
        payload = { kind, spec };
      } else {
        const idx = Number(choice);
        const candidate = candidates![idx];
        payload = { kind: candidate.kind, spec: candidate.spec };
      }
      return {
        type: 'choose_format_spec',
        payload,
        reviewer_id: reviewerId,
      };
    }
    // Legacy options path — choose_format with the string choice.
    if (hasOptions) {
      return {
        type: 'choose_format',
        payload: { choice },
        reviewer_id: reviewerId,
      };
    }
    // No options anywhere → plain accept (reviewer acknowledges).
    return { type: 'accept', reviewer_id: reviewerId };
  };

  const onConfirmClick = () => {
    // v0.6 Stage 1.x.2 — cleanser_quality HITLs always go through the
    // options picker (they don't have a meaningful "recommended" pre-
    // selection — the reviewer must consciously pick adjust / drop /
    // accept). confirmDisabled gates the null case upstream.
    if (isCleanserQuality && selected !== null) {
      return dispatch(chooseFromSelection(selected));
    }
    // Recommended tier default: pre-select the first candidate/option
    // without requiring an explicit radio pick.
    if (tier === 'recommended' && !specifyOpen) {
      if (hasCandidates) {
        return dispatch(chooseFromSelection('0'));
      }
      if (hasOptions) {
        return dispatch(chooseFromSelection(hit.options![0]));
      }
      return dispatch({ type: 'accept', reviewer_id: reviewerId });
    }
    // Unclear / legacy / recommended-with-specify-open → use the picked
    // selection.
    if (hasCandidates && selected !== null) {
      return dispatch(chooseFromSelection(selected));
    }
    if (hasOptions && selected !== null) {
      return dispatch(chooseFromSelection(selected));
    }
    return dispatch({ type: 'accept', reviewer_id: reviewerId });
  };

  const onNoFormatRequiredClick = () => {
    // v0.3 Stage E action type — reviewer explicitly opts out of format
    // enforcement. Persisted to the audit trail and carried forward on
    // re-uploads via (file_hash, column).
    return dispatch({
      type: 'no_format_required',
      reviewer_id: reviewerId,
    });
  };

  const showPicker =
    !resolved &&
    ((tier === 'recommended' && specifyOpen) || tier !== 'recommended') &&
    (hasCandidates || hasOptions);

  // v0.6 Stage F.3 — keyboard navigation. The card is focusable via
  // ``tabIndex={0}`` so reviewers can drive the queue from the
  // keyboard alone. The handler intercepts when the event target is
  // the card itself (``e.target === e.currentTarget``) so that
  // typing into the regex editor / radio buttons inside the body
  // doesn't trigger nav by accident — the j/k letters are common
  // in custom regex patterns and option names.
  //
  // Keymap (matches dev plan §3.6.3):
  //   * ``j`` / ``J`` — focus next card in the queue
  //   * ``k`` / ``K`` — focus previous card
  //   * ``Enter``    — expand the focused card (no-op if already expanded)
  //   * ``Escape``   — collapse the focused card (no-op if already collapsed)
  //   * ``Space``    — click Confirm on the focused card when expanded +
  //                    Confirm is enabled; no-op otherwise. Default-confirm
  //                    on recommended tier pre-selects the first option
  //                    (see ``onConfirmClick``), so Space on a recommended
  //                    card is the canonical one-key confirm shortcut.
  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.target !== e.currentTarget) return;
    const card = e.currentTarget;
    switch (e.key) {
      case 'j':
      case 'J': {
        e.preventDefault();
        focusCardSibling(card, +1);
        break;
      }
      case 'k':
      case 'K': {
        e.preventDefault();
        focusCardSibling(card, -1);
        break;
      }
      case 'Enter': {
        e.preventDefault();
        if (cardCollapsed) toggleCardCollapsed();
        break;
      }
      case 'Escape': {
        e.preventDefault();
        if (!cardCollapsed) toggleCardCollapsed();
        break;
      }
      case ' ': {
        e.preventDefault();
        if (!cardCollapsed) {
          const confirmBtn = card.querySelector<HTMLButtonElement>(
            '[data-testid="hit-confirm"]',
          );
          if (confirmBtn && !confirmBtn.disabled) confirmBtn.click();
        }
        break;
      }
    }
  };

  return (
    <li
      data-hitl-tier={hit.hitl_tier ?? 'legacy'}
      data-hitl-card="true"
      data-testid={`hit-${hit.hit_id}-card`}
      tabIndex={0}
      onKeyDown={handleCardKeyDown}
      className={`rounded-md border bg-paper ${tierStyle.row} p-4 space-y-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-2 focus-visible:ring-offset-paper ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {/*
            * v0.6 Stage F.3 — per-card collapse toggle. The chevron is
            * the standard disclosure pattern; the button is the
            * accessible disclosure surface (``aria-expanded`` +
            * ``aria-controls``). Header stays visible when collapsed so
            * the reviewer can still scan severity / tier / column at a
            * glance and know which card they're parking.
            */}
          <button
            type="button"
            onClick={toggleCardCollapsed}
            aria-expanded={!cardCollapsed}
            aria-controls={`hit-${hit.hit_id}-body`}
            aria-label={cardCollapsed ? 'Expand card' : 'Collapse card'}
            data-testid={`hit-${hit.hit_id}-collapse-toggle`}
            className="text-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded-sm px-0.5"
          >
            <span aria-hidden="true">{cardCollapsed ? '▶' : '▼'}</span>
          </button>
          <span
            className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold uppercase ${severityStyles[hit.severity]}`}
          >
            {hit.severity}
          </span>
          {tierStyle.label && (
            <span
              className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold uppercase ${
                tier === 'recommended'
                  ? 'bg-brand/10 text-brand-dark'
                  : 'bg-warn/10 text-warn'
              }`}
            >
              {tierStyle.label}
            </span>
          )}
          <code className="font-semibold text-ink">{hit.column}</code>
          <span className="text-xs text-muted">· {hit.agent_id}</span>
          {resolved && <span className="text-xs text-brand-dark">resolved</span>}
        </div>
        <code className="text-xs text-muted">{hit.hit_id}</code>
      </div>

      {/*
        * v0.6 Stage F.3 — collapsible body. Wraps everything below
        * the header (recommended label, whyAsking, issue, sample
        * rows, picker, action buttons). Uses ``hidden`` rather than
        * conditional render so the element stays in the DOM for the
        * header's ``aria-controls`` reference + screen-reader
        * discovery semantics. ``hidden`` also removes the body's
        * form controls from the tab order when collapsed, which is
        * the right behaviour (a parked card shouldn't be tabable
        * past its chevron).
        */}
      <div
        id={`hit-${hit.hit_id}-body`}
        className="space-y-3"
        hidden={cardCollapsed}
      >
      {tier === 'recommended' && recommendedLabel && (
        <p className="text-sm">
          <span className="font-medium">Recommended:</span>{' '}
          <code className="font-mono">{recommendedLabel}</code>
        </p>
      )}
      {tier === 'unclear' && (
        <p className="text-sm font-medium text-warn">
          Format unclear — please confirm
        </p>
      )}

      {/*
        * v0.5 Stage D.4 — "Why am I being asked?" copy, always
        * visible on every card. The whyAsking dispatch returns a
        * contextualised string keyed off resolution_type / tier /
        * options shape so each card type gets a tailored
        * explanation.
        */}
      <p
        data-testid="hitl-why-asking"
        className="text-xs text-muted italic border-l-2 border-border pl-3"
      >
        {whyAsking(hit)}
      </p>

      <p className="text-sm">{hit.issue}</p>

      {tier === 'recommended' && hit.representative_example && (
        <div className="rounded border border-border bg-surface-alt/30 px-3 py-2">
          <div className="text-xs uppercase tracking-wide text-muted">
            Representative example
          </div>
          <code className="block font-mono text-sm mt-0.5">
            {hit.representative_example}
          </code>
        </div>
      )}

      {tier !== 'recommended' && hit.sample_rows.length > 0 && (
        <div className="rounded border border-border bg-surface-alt/30 overflow-hidden">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-surface-alt/60">
                <th className="text-left font-medium px-3 py-1.5 text-muted">row</th>
                <th className="text-left font-medium px-3 py-1.5 text-muted">original</th>
                <th className="text-left font-medium px-3 py-1.5 text-muted">cleaned</th>
              </tr>
            </thead>
            <tbody>
              {hit.sample_rows.slice(0, 5).map((r, i) => (
                <tr key={i} className="odd:bg-surface-alt/20">
                  <td className="px-3 py-1.5 font-mono text-muted">{String(r.row_id)}</td>
                  <td className="px-3 py-1.5 font-mono">{formatCell(r.original)}</td>
                  <td className="px-3 py-1.5 font-mono">{formatCell(r.cleaned)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPicker && hasCandidates && !isCleanserQuality && (
        <fieldset
          className="rounded border border-border bg-surface-alt/20 p-3"
          disabled={pending !== null}
        >
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted">
            Pick a format
          </legend>
          <div role="radiogroup" className="flex flex-col gap-1.5 pt-1">
            {candidates!.map((candidate, idx) => {
              const value = String(idx);
              const inputId = `${hit.hit_id}-fs-${idx}`;
              const label =
                candidate.display_spec ??
                (typeof candidate.spec === 'object' && candidate.spec !== null
                  ? JSON.stringify(candidate.spec)
                  : String(candidate.spec));
              const confidence =
                typeof candidate.confidence === 'number'
                  ? ` · ${(candidate.confidence * 100).toFixed(0)}% confidence`
                  : '';
              return (
                <label
                  key={value}
                  htmlFor={inputId}
                  className="inline-flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    id={inputId}
                    type="radio"
                    name={`hit-${hit.hit_id}-fs`}
                    value={value}
                    checked={selected === value}
                    onChange={() => setSelected(value)}
                    className="accent-brand-dark"
                  />
                  <span>
                    <span className="font-mono">{label}</span>
                    <span className="text-xs text-muted">
                      {' '}
                      · {candidate.kind}
                      {confidence}
                    </span>
                  </span>
                </label>
              );
            })}
            {customSpecAvailable && (
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor={`${hit.hit_id}-fs-custom`}
                  className="inline-flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    id={`${hit.hit_id}-fs-custom`}
                    type="radio"
                    name={`hit-${hit.hit_id}-fs`}
                    value={CUSTOM_SENTINEL}
                    checked={selected === CUSTOM_SENTINEL}
                    onChange={() => setSelected(CUSTOM_SENTINEL)}
                    className="accent-brand-dark"
                  />
                  <span className="text-sm">Custom:</span>
                </label>
                {/*
                 * v0.5 Stage A: detector-aware Custom-input UI.
                 * Replaces the prior single-string `{value: <input>}`
                 * payload that was silently discarded by every cleanser
                 * except string_cleaner (regex_mask). The component
                 * renders different fields per kind (two inputs for
                 * boolean labels, comma-separated CSV for enum, etc.)
                 * and notifies us via `onChange` with a validated
                 * `{ok, spec}` result. Confirm is gated on `ok=true`
                 * via `customSpecValid` above.
                 */}
                <div className="ml-6">
                  <CustomSpecInput
                    kind={customSpecKind as Parameters<typeof CustomSpecInput>[0]['kind']}
                    idPrefix={hit.hit_id}
                    disabled={pending !== null || selected !== CUSTOM_SENTINEL}
                    onChange={(result) => {
                      setCustomSpecResult(result);
                      // Auto-select the Custom radio when the reviewer
                      // starts typing into a field — saves a click.
                      if (result.ok && selected !== CUSTOM_SENTINEL) {
                        setSelected(CUSTOM_SENTINEL);
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </fieldset>
      )}
      {showPicker && (isCleanserQuality || !hasCandidates) && hasOptions && (
        <fieldset
          className="rounded border border-border bg-surface-alt/20 p-3"
          disabled={pending !== null}
        >
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted">
            {isCleanserQuality ? 'Resolve column quality' : 'Choose a value'}
          </legend>
          <div role="radiogroup" className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
            {hit.options!.map((opt) => {
              const inputId = `${hit.hit_id}-opt-${opt}`;
              const isMixed = opt === MIXED_TYPES_OPTION;
              return (
                <label
                  key={opt}
                  htmlFor={inputId}
                  className="inline-flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    id={inputId}
                    type="radio"
                    name={`hit-${hit.hit_id}-option`}
                    value={opt}
                    checked={selected === opt}
                    onChange={() => setSelected(opt)}
                    className="accent-brand-dark"
                  />
                  <span className="font-mono">
                    {PII_OPTION_LABELS[opt] ?? opt}
                    {isMixed && (
                      <span className="ml-1 text-xs text-muted">
                        (multiple types)
                      </span>
                    )}
                    {cleanserOptionDescriptions[opt] && (
                      <span
                        className="ml-1 text-xs text-muted"
                        data-testid={`option-description-${opt}`}
                      >
                        — {cleanserOptionDescriptions[opt]}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          {/*
            * v0.5 Stage C.2 — when the reviewer selects ``mixed_types``,
            * reveal the checkbox-group selector so they can pick which
            * types to cleanse. The selector reports the picked union +
            * primary via ``setMixedTypeSelection``; the dispatch
            * builder reads it on Confirm to produce the
            * ``choose_mixed_types`` payload.
            */}
          {selected === MIXED_TYPES_OPTION && (
            <MixedTypeSelector
              candidateTypes={hit.options!.filter(
                (opt) => !MIXED_TYPE_SELECTOR_EXCLUDED.has(opt),
              )}
              onChange={setMixedTypeSelection}
              disabled={pending !== null}
            />
          )}
          {/*
            * v0.6 Stage 1.x.2 — adjust_spec inline editor. When the
            * reviewer picks the "adjust_spec" radio on a cleanser_quality
            * HITL, surface the original regex pre-filled so they can
            * iterate on it (typo fix, anchor change, etc) instead of
            * retyping from scratch. Confirm enablement (gated above on
            * non-empty pattern) prevents an empty submit.
            */}
          {isCleanserQuality && selected === 'adjust_spec' && (
            <div className="mt-3 flex flex-col gap-1.5">
              <label
                htmlFor={`${hit.hit_id}-adjust-spec`}
                className="text-xs uppercase tracking-wide text-muted"
              >
                Adjusted pattern (regex)
              </label>
              <input
                id={`${hit.hit_id}-adjust-spec`}
                type="text"
                value={cleanserAdjustSpecText}
                onChange={(e) => setAdjustSpecDraft(e.target.value)}
                disabled={pending !== null}
                className="font-mono text-sm rounded border border-border bg-paper px-2 py-1.5"
                placeholder={cleanserOriginalPattern || 'e.g. ^SYS-\\d+$'}
                spellCheck={false}
              />
              {cleanserOriginalPattern && (
                <span className="text-xs text-muted">
                  Original: <code className="font-mono">{cleanserOriginalPattern}</code>
                </span>
              )}
            </div>
          )}
        </fieldset>
      )}

      {!resolved && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="primary"
            onClick={onConfirmClick}
            disabled={confirmDisabled}
            data-testid="hit-confirm"
            title={
              cleanserAdjustSpecMissing
                ? 'Type an adjusted pattern before confirming'
                : isCleanserQuality && selected === null
                  ? 'Pick a resolution above before confirming'
                  : confirmDisabled && tier !== 'recommended' && hasCandidates && !pickerReady
                    ? 'Pick a candidate (or type a custom spec) before confirming'
                    : confirmDisabled && tier !== 'recommended' && hasOptions && selected === null
                      ? 'Select a value above before confirming'
                      : undefined
            }
          >
            {pending === 'accept' ||
            pending === 'choose_format' ||
            pending === 'choose_format_spec'
              ? 'Confirming…'
              : 'Confirm'}
          </Button>
          {tier === 'recommended' && !specifyOpen && (hasCandidates || hasOptions) && (
            <Button
              variant="ghost"
              onClick={() => setSpecifyOpen(true)}
              disabled={pending !== null}
            >
              Not correct — specify
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={onNoFormatRequiredClick}
            disabled={pending !== null}
            title="Skip format enforcement for this column"
          >
            {pending === 'no_format_required'
              ? 'Submitting…'
              : 'Retain data in current format'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => dispatch({ type: 'escalate', reviewer_id: reviewerId })}
            disabled={pending !== null}
          >
            {pending === 'escalate' ? 'Escalating…' : 'Escalate'}
          </Button>
          {/*
            * v0.6 Stage G.3 — Revisit decision button for
            * cleanser_quality HITLs only. Mirror of RuleCard's
            * "Revisit rule" affordance: dispatches
            * `revisit_cleanser_decision`, whose dispatch helper in
            * `routers/hits.py` looks up the prior ack'd cleanser hit
            * (by (file_hash, agent_id, column, threshold_signal)) and
            * writes a sibling audit row on THAT hit_id. The v0.6 G.2
            * carry-forward query's latest-event-wins filter drops the
            * column from the ack set on the next pipeline pass and the
            * cleanser-quality HITL re-emerges.
            *
            * Surfaced only on cleanser cards: other resolution_types
            * (format_decision, mixed_types, column_datatype) don't
            * carry a threshold_signal so the dispatcher would fail
            * soft on them; hiding the button avoids the no-op click.
            */}
          {isCleanserQuality && (
            <Button
              variant="ghost"
              onClick={() =>
                dispatch({
                  type: 'revisit_cleanser_decision',
                  reviewer_id: reviewerId,
                })
              }
              disabled={pending !== null}
              data-testid="cleanser-revisit-decision"
              title="Re-open this cleanser decision. The prior accept is invalidated so the HITL re-emerges on the next run."
            >
              {pending === 'revisit_cleanser_decision'
                ? 'Reopening decision…'
                : 'Revisit decision'}
            </Button>
          )}
          {error && (
            <span role="alert" className="text-xs text-danger">
              {error}
            </span>
          )}
        </div>
      )}
      </div>
    </li>
  );
}

function recommendedHeaderLabel(hit: HitlItem): string | null {
  // Prefer the first candidate's display_spec / kind (FormatInferrer
  // hits), falling back to the first option (RAS column_datatype hits).
  // Cross-field rule HITLs are routed away by the parent before reaching
  // this fn, so the cast to FormatSpecCandidate is safe.
  if (Array.isArray(hit.candidates) && hit.candidates.length > 0) {
    const c = hit.candidates[0] as FormatSpecCandidate;
    return c.display_spec ?? c.kind ?? null;
  }
  if (Array.isArray(hit.options) && hit.options.length > 0) {
    return hit.options[0];
  }
  return null;
}

function formatCell(v: string | number | null | undefined): React.ReactNode {
  if (v === null || v === undefined || v === '') {
    return <span className="text-muted italic">—</span>;
  }
  return String(v);
}
